import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Tag,
  Button,
  Space,
  DatePicker,
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Alert,
  Progress,
} from 'antd';
import {
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import { fetchPriceInflationRequest } from '../../redux/actions/priceHistoryActions';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const fmtMoney = (val) =>
  val != null ? `₹ ${Number(val).toFixed(2)}` : '—';

// ── Change percent tag ────────────────────────────────────────────────────────

const ChangeTag = ({ pct }) => {
  const p = Number(pct) || 0;
  if (p > 20) return <Tag color="error" icon={<ArrowUpOutlined />}>+{p.toFixed(2)}%</Tag>;
  if (p > 10) return <Tag color="warning" icon={<ArrowUpOutlined />}>+{p.toFixed(2)}%</Tag>;
  if (p > 0)  return <Tag color="default" icon={<ArrowUpOutlined />} style={{ color: '#fa8c16' }}>+{p.toFixed(2)}%</Tag>;
  if (p < 0)  return <Tag color="success" icon={<ArrowDownOutlined />}>{p.toFixed(2)}%</Tag>;
  return <Tag>0%</Tag>;
};

const InflationBar = ({ pct }) => {
  const p = Math.min(100, Math.abs(Number(pct) || 0));
  const color = pct > 20 ? '#ff4d4f' : pct > 10 ? '#faad14' : pct > 0 ? '#fa8c16' : '#52c41a';
  return (
    <div style={{ minWidth: 80 }}>
      <Progress
        percent={p}
        showInfo={false}
        size="small"
        strokeColor={color}
        trailColor="#f0f0f0"
      />
    </div>
  );
};

const PriceInflationReport = () => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { inflationReport, loading } = useSelector((s) => s.priceHistory);

  const canView = hasPermission(permissions, 'purchase_order/view');
  const [dateRange, setDateRange] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (dateRange?.[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.date_to   = dateRange[1].format('YYYY-MM-DD');
    dispatch(fetchPriceInflationRequest(params));
  }, [dispatch, dateRange]);

  useEffect(() => {
    if (canView) load();
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: 60, color: '#8c8c8c' }}>
          You don't have permission to view price reports.
        </div>
      </div>
    );
  }

  // Stats
  const highInflation  = inflationReport.filter((r) => r.change_percent > 20).length;
  const midInflation   = inflationReport.filter((r) => r.change_percent > 10 && r.change_percent <= 20).length;
  const avgChange      = inflationReport.length
    ? inflationReport.reduce((s, r) => s + r.change_percent, 0) / inflationReport.length
    : 0;
  const maxChange      = inflationReport.length
    ? Math.max(...inflationReport.map((r) => r.change_percent))
    : 0;

  const columns = [
    {
      title: '#',
      key: 'rank',
      width: 46,
      render: (_, __, i) => (
        <Text type="secondary" style={{ fontSize: 11 }}>{i + 1}</Text>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (name, record) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{name}</Text>
          {record.unit && (
            <Tag style={{ marginLeft: 6, fontSize: 10 }} color="geekblue">{record.unit}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Starting Rate',
      dataIndex: 'first_rate',
      key: 'first_rate',
      width: 120,
      align: 'right',
      render: (v) =>
        v === 0 ? (
          <Text type="secondary" style={{ fontSize: 11 }}>New Product</Text>
        ) : (
          <Text style={{ color: '#8c8c8c', fontSize: 12 }}>{fmtMoney(v)}</Text>
        ),
    },
    {
      title: 'Current Rate',
      dataIndex: 'latest_rate',
      key: 'latest_rate',
      width: 120,
      align: 'right',
      render: (v) => (
        <Text strong style={{ color: '#1890ff', fontSize: 13 }}>{fmtMoney(v)}</Text>
      ),
    },
    {
      title: 'Change',
      dataIndex: 'change_percent',
      key: 'change_percent',
      width: 100,
      align: 'center',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.change_percent - b.change_percent,
      render: (v) => <ChangeTag pct={v} />,
    },
    {
      title: 'Inflation Bar',
      dataIndex: 'change_percent',
      key: 'bar',
      width: 120,
      render: (v) => <InflationBar pct={v} />,
    },
    {
      title: 'Price Changes',
      dataIndex: 'total_changes',
      key: 'total_changes',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.total_changes - b.total_changes,
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><LineChartOutlined /></div>
            <div className="page-header-text">
              <h1>Price Inflation Report</h1>
              <p>Track purchase rate changes across all products — spot inflation early</p>
            </div>
          </div>
        </div>

        {/* ── Summary cards ────────────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Products Tracked"
                value={inflationReport.length}
                prefix={<BarChartOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ background: '#fff2f0', borderColor: '#ffadd2' }}>
              <Statistic
                title="> 20% Inflation"
                value={highInflation}
                prefix={<ArrowUpOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
              <Statistic
                title="10–20% Inflation"
                value={midInflation}
                prefix={<ArrowUpOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic
                title="Avg Change"
                value={`${Number(avgChange).toFixed(1)}%`}
                valueStyle={{ color: avgChange > 10 ? '#ff4d4f' : avgChange > 0 ? '#fa8c16' : '#52c41a', fontSize: 18 }}
              />
            </Card>
          </Col>
        </Row>

        {/* ── Warning banner ────────────────────────────────────────────── */}
        {highInflation > 0 && (
          <Alert
            type="error"
            showIcon
            closable
            message={`${highInflation} product(s) have price increases above 20% — review immediately`}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* ── Filters ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <Title level={5} style={{ margin: 0 }}>Inflation by Product</Title>
          <Space wrap>
            <RangePicker
              style={{ width: 260 }}
              format="DD MMM YYYY"
              value={dateRange}
              onChange={setDateRange}
              allowClear
              placeholder={['From Date', 'To Date']}
            />
            <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Apply
            </Button>
            {dateRange && (
              <Button icon={<ClockCircleOutlined />} onClick={() => setDateRange(null)}>
                All Time
              </Button>
            )}
          </Space>
        </div>

        {/* ── Legend ────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Colour guide:</Text>
          <Tag color="error">{'> 20%'} High inflation</Tag>
          <Tag color="warning">10–20% Medium</Tag>
          <Tag color="default" style={{ color: '#fa8c16' }}>{'< 10%'} Low</Tag>
          <Tag color="success">Decrease</Tag>
        </div>

        {/* ── Table ─────────────────────────────────────────────────────── */}
        <Table
          columns={columns}
          dataSource={inflationReport}
          rowKey="product_id"
          loading={loading}
          size="small"
          pagination={{ pageSize: 25, showSizeChanger: false }}
          scroll={{ x: 780 }}
          rowClassName={(record) => {
            const p = record.change_percent;
            if (p > 20) return 'inflation-row-high';
            if (p > 10) return 'inflation-row-mid';
            return '';
          }}
          locale={{
            emptyText: (
              <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                No price history found. Prices are recorded automatically when products are updated.
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default PriceInflationReport;
